import { useState, useEffect, useRef } from "react";
import SvgMapper from "../../features/common/SvgMapper";
import { useSelector, useDispatch } from "react-redux";
import { setInterviewModal, setResume, setActiveInterview, updateInterviewResume } from "../../../store/studentPageReducer";
import InterviewSetup from "../InterviewSetup/InterviewSetup";
import useActiveInterviews from "../../hooks/useActiveInterviews";
import { formatDateTime } from "../dateFormat";
import { updateScheduleInterview } from "../../../application/services/scheduleTabService";
import { SWAGGER_URL } from "../../../common/apiBaseUrl";

const difficultyColors = {
  beginner: "text-green-600 bg-green-50",
  intermediate: "text-yellow-600 bg-yellow-50",
  advanced: "text-red-600 bg-red-50",
};

const AvailableInterviews = () => {
  const dispatch = useDispatch();
  const { openInterviewModal } = useSelector(
    (state) => state.studentpage
  );
  const { user } = useSelector((state) => state.login.student);
  const email = user?.email || "";
  const { upcomingInterviews, loadMore, hasMore, loading, fetchActiveInterviews } = useActiveInterviews();
  const [uploadingStates, setUploadingStates] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const [resumeUploaded, setResumeUploaded] = useState({});
  const scrollRef = useRef(null);
  const pollingRef  = useRef({});

  useEffect(() => {
    
    if (!email) {
      return;
    }

    if (!fetchActiveInterviews) {
      return;
    }

    fetchActiveInterviews(1, false).catch(error => {
      console.error("Error calling fetchActiveInterviews:", error);
    });
  }, []);

  useEffect(() => {
    if (email && fetchActiveInterviews) {
      fetchActiveInterviews(1, false).catch(error => {
        console.error("Error calling fetchActiveInterviews:", error);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  useEffect(() => {
    if (!scrollRef.current) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      if (scrollHeight - scrollTop - clientHeight < 100 && hasMore && !loading) {
        loadMore();
      }
    };

    const container = scrollRef.current;
    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [hasMore, loading, loadMore]);

  useEffect(() => {
    upcomingInterviews.forEach((interview) => {
      const interviewId =
        interview.id ||
        interview.interview_id ||
        interview.schedule_id ||
        interview.interview_candidate_mapping_id;

      if (!interviewId) return;

      const hasResume = !!interview.resume_url;
      const isGenerated =
        interview.is_question_generated === true ||
        interview.is_question_generated === "true";

      if (hasResume && !isGenerated && !pollingRef.current[interviewId]) {
        pollingRef.current[interviewId] = setInterval(() => {
          fetchActiveInterviews(1, false);
        }, 2 * 60 * 1000);
      }

      if (isGenerated && pollingRef.current[interviewId]) {
        clearInterval(pollingRef.current[interviewId]);
        delete pollingRef.current[interviewId];
      }
    });
  }, [upcomingInterviews]);

  useEffect(() => {
  return () => {
    const intervals = pollingRef.current || {};
    Object.values(intervals).forEach((interval) => clearInterval(interval));
  };
}, []);

  const uploadResumeToS3 = async (file) => {
    try {
      const formData = new FormData();
      formData.append("folder", "resumes");
      formData.append("file", file);

      const timestamp = Date.now();
      const response = await fetch(
        `${SWAGGER_URL}/storage/add-external`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) throw new Error("File upload failed");
      const data = await response.json();
      return data?.url || data?.fileUrl || null;
    } catch (error) {
      console.error("Error uploading resume to S3:", error);
      return null;
    }
  };

  const handleResumeUpload = async (e, interviewIndex) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setUploadErrors(prev => ({ ...prev, [interviewIndex]: "Only PDF files are allowed" }));
      e.target.value = "";
      return;
    }
    const minSize = 1024;
    if (file.size === 0 || file.size < minSize) {
      setUploadErrors(prev => ({ ...prev, [interviewIndex]: "File cannot be empty" }));
      e.target.value = "";
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadErrors(prev => ({ ...prev, [interviewIndex]: "File size must be less than 10MB" }));
      e.target.value = "";
      return;
    }

    setUploadErrors(prev => ({ ...prev, [interviewIndex]: "" }));
    setUploadingStates(prev => ({ ...prev, [interviewIndex]: true }));

    try {
      const uploadedUrl = await uploadResumeToS3(file);
      
      if (uploadedUrl) {
        const interview = upcomingInterviews[interviewIndex];
        dispatch(setResume(uploadedUrl));
        dispatch(updateInterviewResume({ interviewIndex, resumeUrl: uploadedUrl }));
        const updatedInterview = {
          ...interview,
          resume_url: uploadedUrl
        };
        dispatch(setActiveInterview(updatedInterview));
        const interviewId = interview.id || interview.interview_id || interview.schedule_id || interview.interview_candidate_mapping_id;
        if (interviewId) {
          try {
            const payload = {
              resume_url: uploadedUrl
            };
            await updateScheduleInterview(interviewId, payload);
            fetchActiveInterviews(1, false);
          } catch (apiError) {
            console.error("Error updating scheduled interview:", apiError);
            setUploadErrors(prev => ({ ...prev, [interviewIndex]: "Resume uploaded but failed to save. Please try again." }));
          }
        }
      } else {
        setUploadErrors(prev => ({ ...prev, [interviewIndex]: "Failed to upload resume. Please try again." }));
      }
    } catch (error) {
      console.error("Error uploading resume:", error);
      setUploadErrors(prev => ({ ...prev, [interviewIndex]: "Failed to upload resume. Please try again." }));
    } finally {
      setUploadingStates(prev => ({ ...prev, [interviewIndex]: false }));
      e.target.value = "";
    }
  };

  const handleStartInterview = (interview) => {
    dispatch(setActiveInterview(interview));
    dispatch(setInterviewModal(true));
  };

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Available Interviews
        </h3>

        {!upcomingInterviews || upcomingInterviews.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 text-gray-300">
              {SvgMapper.interviewIcon("w-16 h-16")}
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Active Interviews
            </h3>
            <p className="text-gray-600">
              There are no interviews available at the moment. Check back later for new interview opportunities.
            </p>
          </div>
        ) : (
          <div 
            ref={scrollRef}
            className="max-h-[350px] overflow-y-scroll custom-scrollbar"
            style={{ scrollBehavior: 'smooth', paddingRight: '8px' }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingInterviews.map((interview, index) => (
            <div
              key={index}
              className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    {interview.interview_name}
                  </h4>

                  <div className="flex items-center space-x-2 mb-3">
                    <span className="px-2 py-1 rounded-full text-xs font-medium text-blue-600 bg-blue-50">
                      {interview.category}
                    </span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        difficultyColors[interview.difficulty?.toLowerCase()] ||
                        "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {interview.difficulty}
                    </span>
                  </div>

                  <div className="flex items-center text-gray-500 mb-2">
                    {SvgMapper.clock("w-4 h-4 mr-1")}
                    <span>{interview.duration} min</span>
                  </div>
                  {(interview.active_from || interview.active_to) && (
                    <div className="text-sm space-y-1">
                      {interview.active_from && (
                        <div className="flex items-center">
                          <div className="text-purple-500 mr-1 flex-shrink-0">
                            {SvgMapper.calendar("w-4 h-4")}
                          </div>
                          <span className="text-xs text-gray-700">
                            <span className="font-semibold text-purple-600 inline-block w-20">Active From:</span> 
                            <span className="text-purple-600 font-medium">{formatDateTime(interview.active_from)}</span>
                          </span>
                        </div>
                      )}
                      {interview.active_to && (
                        <div className="flex items-center">
                          <div className="w-4 mr-1"></div>
                          <span className="text-xs text-gray-700">
                            <span className="font-semibold text-purple-600 inline-block w-20">Active To:</span> 
                            <span className="text-purple-600 font-medium">{formatDateTime(interview.active_to)}</span>
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {(() => {
                const hasResume = !!interview.resume_url;
                const isActive = interview.is_active === true || interview.is_active === "true";
                const isQuestionGenerated = interview.is_question_generated === true || interview.is_question_generated === "true";
                const canStartInterview = hasResume && isActive && isQuestionGenerated;
                const isUploading = uploadingStates[index];
                const uploadError = uploadErrors[index];
                const showWaitingMessage = hasResume && !isQuestionGenerated;

                return !hasResume ? (
                  <div className="flex flex-col gap-3">
                    <div>
                      <label className={`w-full px-6 py-3 rounded-lg font-semibold text-sm transition-colors inline-flex items-center justify-center ${
                        isUploading
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                          : "bg-yellow-400 text-black cursor-pointer hover:bg-yellow-300"
                      }`}>
                        {isUploading ? "Uploading..." : "Upload Resume"}
                        <input
                          type="file"
                          accept="application/pdf"
                          className="hidden"
                          onChange={(e) => handleResumeUpload(e, index)}
                          disabled={isUploading}
                        />
                      </label>
                      {uploadError && (
                        <p className="text-red-500 text-xs mt-1">{uploadError}</p>
                      )}
                      {showWaitingMessage  && !uploadError && (
                        <p className="text-green-600 text-xs mt-1 font-medium">
                          ✓ Your resume has been uploaded successfully. Please wait for few minutes to start the interview.
                        </p>
                      )}
                    </div>
                    <button
                      disabled
                      className="w-full bg-gray-300 text-gray-500 px-6 py-3 rounded-lg font-medium text-sm flex items-center justify-center cursor-not-allowed"
                    >
                      {SvgMapper.play("w-5 h-5")}
                      <span className="ml-2">Start Interview</span>
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {showWaitingMessage  && (
                      <p className="text-green-600 text-xs font-medium text-center">
                        ✓ Your resume has been uploaded successfully. Please wait for few minutes to start the interview.
                      </p>
                    )}
                    <button
                      disabled={!canStartInterview}
                      onClick={() => canStartInterview && handleStartInterview(interview)}
                      className={`w-full py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${
                        canStartInterview
                          ? "bg-yellow-400 hover:bg-yellow-500 text-white cursor-pointer"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      {SvgMapper.play("w-5 h-5")}
                      <span>Start Interview</span>
                    </button>
                  </div>
                );
              })()}
            </div>
          ))}
            </div>
            
            {loading && (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-400"></div>
                <span className="ml-3 text-gray-600">Loading more interviews...</span>
              </div>
            )}
            
            {!hasMore && upcomingInterviews.length > 0 && (
              <div className="text-center py-4 text-gray-500 text-sm">
                No more interviews to load
              </div>
            )}
          </div>
        )}
      </div>
      {openInterviewModal && <InterviewSetup />}
    </>
  );
};

export default AvailableInterviews;
